import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { LogoPreview } from '@/components/form/fields/image/LogoInput';
import { ToggleField } from '@/components/form/fields/toggle/Toggle';
import { useModal } from '@/components/modal/useModal';
import { Step } from '@/components/multistep/Questionnaire';
import { useGetLogo } from '@/services/api/hooks/images';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { FunctionComponent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CreateMSPFormFields } from '../CreateMspPage';
import { EditMSPFormFields } from '../EditMspWizard';
import UploadMspLogoModal from './UploadMspLogoModal';
import { useURLFromFile } from '@/components/form/fields/image/useURLFromFile';

interface UploadMspLogoStepProps {
  mspId?: string;
}

const UploadMspLogoStep: FunctionComponent<UploadMspLogoStepProps> = ({ mspId }) => {
  const [defaultLogoUrl] = useGetLogo(mspId);

  const readImageUrl = useURLFromFile();
  const [logoUrl, setLogoUrl] = useState<string | null>(defaultLogoUrl);

  const onLogoUpdate = async (file: File) => {
    const url = (await readImageUrl(file)) as string;
    setLogoUrl(url);
  };

  const { setValue, watch } = useFormContext<EditMSPFormFields | CreateMSPFormFields>();

  const onLogoClear = () => {
    // Not ideal
    setValue('logo', false);
    setLogoUrl(null);
  };

  const uploadMspLogoModalProps = useModal<{ onConfirmCallback: (file: File) => void | Promise<void> }>();
  const onUploadMspLogoModalOpen = () => uploadMspLogoModalProps.open({ onConfirmCallback: onLogoUpdate });

  const canUseLogo = watch('allowCustomLogo');

  const role = useRole();
  const isPartner = role.type === RoleType.Partner;

  return (
    <>
      <UploadMspLogoModal {...uploadMspLogoModalProps} />
      <Step id="companyLogo" title="Company Logo">
        <div className="flex flex-1 flex-col space-y-4">
          {!isPartner && (
            <div className="flex space-x-2 items-center">
              <ToggleField id="allowCustomLogo" />
              <p>Allow MSP to use a custom logo</p>
            </div>
          )}
          {canUseLogo &&
            (logoUrl ? (
              <>
                <LogoPreview url={logoUrl} />
                <div className="flex flex-col space-y-2">
                  <Button
                    className="w-56"
                    disabled={!canUseLogo}
                    onClick={onUploadMspLogoModalOpen}
                    variant={ButtonVariant.Mono}
                  >
                    Replace
                  </Button>
                  <Button className="w-56" disabled={!canUseLogo} onClick={onLogoClear} variant={ButtonVariant.Mono}>
                    Remove
                  </Button>
                </div>
              </>
            ) : (
              <Button
                className="w-56"
                disabled={!canUseLogo}
                onClick={onUploadMspLogoModalOpen}
                variant={ButtonVariant.Mono}
              >
                Upload Logo
              </Button>
            ))}
        </div>
      </Step>
    </>
  );
};

export default UploadMspLogoStep;
